/**
 * IndexedDB-backed offline queue for DEX Companion syncs.
 * v2.5.883
 *
 * Queues DEX ingestion payloads while offline and flushes them to
 * /api/vending/companion/sync when the browser comes back online.
 *
 * Each payload gets a stable client_id so re-syncs after retries are
 * idempotent on the backend.
 */
import axios from 'axios';

const DB_NAME = 'konnect_dex_companion';
const DB_VERSION = 1;
const STORE = 'sync_queue';
const API = process.env.REACT_APP_BACKEND_URL;

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (ev) => {
      const db = ev.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'client_id' });
        store.createIndex('status', 'status', { unique: false });
        store.createIndex('queued_at', 'queued_at', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function withStore(mode, fn) {
  return openDb().then((db) => {
    if (!db) return null;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const store = tx.objectStore(STORE);
      const result = fn(store);
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => reject(tx.error);
    });
  });
}

export async function enqueueSync(payload) {
  if (!payload.client_id) {
    payload.client_id = `dex-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
  const record = {
    ...payload,
    status: 'queued',
    queued_at: new Date().toISOString(),
    attempts: 0,
  };
  await withStore('readwrite', (store) => store.put(record));
  return record;
}

export async function listQueue() {
  const items = [];
  return withStore('readonly', (store) => {
    return new Promise((resolve) => {
      const req = store.openCursor();
      req.onsuccess = (ev) => {
        const cursor = ev.target.result;
        if (cursor) {
          items.push(cursor.value);
          cursor.continue();
        } else {
          resolve(items);
        }
      };
    });
  }).then(() => items);
}

export async function removeFromQueue(client_id) {
  return withStore('readwrite', (store) => store.delete(client_id));
}

export async function markQueueError(client_id, err) {
  return withStore('readwrite', (store) => {
    const req = store.get(client_id);
    req.onsuccess = () => {
      const rec = req.result;
      if (!rec) return;
      rec.status = 'error';
      rec.last_error = String(err).slice(0, 400);
      rec.attempts = (rec.attempts || 0) + 1;
      rec.updated_at = new Date().toISOString();
      store.put(rec);
    };
  });
}

// v2.5.893 — LOW: prevent concurrent flushes (mount + button + reconnect can
// otherwise fire together). Backend client_id dedupe currently masks it but
// this saves needless round-trips.
let _flushingLock = false;

/**
 * Flush all queued items to backend. Returns { synced, failed, remaining }
 */
export async function flushQueue({ onProgress } = {}) {
  if (_flushingLock) return { synced: 0, failed: 0, remaining: 'busy' };
  _flushingLock = true;
  try {
  const items = await listQueue();
  let synced = 0;
  let failed = 0;
  for (const item of items) {
    if (item.status === 'synced') continue;
    try {
      const { data } = await axios.post(`${API}/api/vending/companion/sync`, {
        machine_id: item.machine_id,
        chassis_serial_hint: item.chassis_serial_hint,
        capture_method: item.capture_method,
        dex_text: item.dex_text,
        parsed_fields: item.parsed_fields,
        device_id: item.device_id,
        device_model: item.device_model,
        session_id: item.session_id,
        client_id: item.client_id,
        notes: item.notes,
        location_lat: item.location_lat,
        location_lng: item.location_lng,
      });
      if (data.ok) {
        await removeFromQueue(item.client_id);
        synced++;
      } else {
        await markQueueError(item.client_id, data.error || 'Unknown error');
        failed++;
      }
    } catch (e) {
      await markQueueError(item.client_id, e.response?.data?.detail || e.message);
      failed++;
    }
    if (onProgress) onProgress({ synced, failed, remaining: items.length - synced - failed });
  }
  return { synced, failed, remaining: items.length - synced - failed };
  } finally {
    _flushingLock = false;
  }
}

export function watchOnlineStatus(onChange) {
  const handler = () => onChange(navigator.onLine);
  window.addEventListener('online', handler);
  window.addEventListener('offline', handler);
  handler();
  return () => {
    window.removeEventListener('online', handler);
    window.removeEventListener('offline', handler);
  };
}
